// src/pages/Admin/PartnerOrganisations.jsx
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Layers3,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  fetchAdminPartnerOrganisations,
  fetchAdminPartnerProgrammeAccess,
  setAdminPartnerProgrammeAccess,
  updateAdminPartnerOrganisation,
} from "../../lib/admin/adminPartners";
import CreatePartnerOrganisationModal from "../../components/admin/CreatePartnerOrganisationModal";
import AdminInvitePartnerMemberModal from "../../components/admin/AdminInvitePartnerMemberModal";
import PartnerMemberInvitationsPanel from "../../components/partner/PartnerMemberInvitationsPanel";

function formatNumber(value) {
  const number = Number(value);

  return Number.isFinite(number)
    ? number.toLocaleString("en-ZA")
    : "0";
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return date.toLocaleString(
    "en-ZA",
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
}

function statusClasses(status) {
  switch (status) {
    case "active":
      return "border-green-200 bg-green-50 text-green-800";

    case "suspended":
      return "border-red-200 bg-red-50 text-red-700";

    default:
      return "border-gray-200 bg-gray-100 text-gray-700";
  }
}

function programmeStatusClasses(status) {
  switch (status) {
    case "active":
      return "border-green-200 bg-green-50 text-green-800";

    case "suspended":
      return "border-red-200 bg-red-50 text-red-700";

    case "draft":
      return "border-amber-200 bg-amber-50 text-amber-800";

    default:
      return "border-gray-200 bg-gray-100 text-gray-700";
  }
}

function programmeStatusLabel(status) {
  switch (status) {
    case "not_configured":
      return "Not configured";

    case "active":
      return "Active";

    case "suspended":
      return "Suspended";

    case "draft":
      return "Draft";

    default:
      return status || "Unknown";
  }
}

function sponsoredAccessLabel(value) {
  switch (value) {
    case "full_journey":
      return "Full journey";

    case "none":
    default:
      return "No sponsored learning";
  }
}

function formatProgrammeUsage(usage, maximum) {
  const used = formatNumber(usage || 0);

  if (maximum === null || maximum === undefined) {
    return `${used} / Unlimited`;
  }

  return `${used} / ${formatNumber(maximum)}`;
}

function SummaryCard({
  label,
  value,
  detail,
  icon: Icon,
}) {
  return (
    <article className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>

          <p className="mt-3 text-3xl font-black text-[#333333]">
            {formatNumber(value)}
          </p>

          {detail && (
            <p className="mt-1 text-xs font-semibold text-[#757575]">
              {detail}
            </p>
          )}
        </div>

        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </article>
  );
}

function ProgrammeAccessModal({
  open,
  partner,
  programmeAccess,
  saving,
  onClose,
  onSave,
}) {
  const hasInitialisedOpen =
    useRef(false);

  const [status, setStatus] =
    useState("draft");

  const [maxCohorts, setMaxCohorts] =
    useState("");

  const [maxLearners, setMaxLearners] =
    useState("");

  const [sponsoredAccessType, setSponsoredAccessType] =
    useState("none");

  const [formError, setFormError] =
    useState("");

  useEffect(() => {
    /*
     * Reset the initialization flag whenever
     * the modal closes.
     */
    if (!open) {
      hasInitialisedOpen.current =
        false;

      return;
    }

    /*
     * Initialise the form only once per modal
     * opening.
     *
     * programmeAccess may be refreshed/replaced
     * while this modal is open. We must NOT let
     * that overwrite values the Admin is
     * currently editing.
     */
    if (
      hasInitialisedOpen.current
    ) {
      return;
    }

    hasInitialisedOpen.current =
      true;

    setStatus(
      programmeAccess?.status ||
        "draft"
    );

    setMaxCohorts(
      programmeAccess?.max_cohorts ===
        null ||
      programmeAccess?.max_cohorts ===
        undefined
        ? ""
        : String(
            programmeAccess.max_cohorts
          )
    );

    setMaxLearners(
      programmeAccess?.max_learners ===
        null ||
      programmeAccess?.max_learners ===
        undefined
        ? ""
        : String(
            programmeAccess.max_learners
          )
    );

    setSponsoredAccessType(
      programmeAccess
        ?.sponsored_access_type ||
        "none"
    );

    setFormError("");
  }, [
    open,
    partner?.id,
    programmeAccess,
  ]);

  if (!open || !partner) {
    return null;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError("");

    try {
      const submittedStatus =
        String(
          event.currentTarget.elements
            .namedItem(
              "allocation-status"
            )?.value ||
            status ||
            "draft"
        )
          .trim()
          .toLowerCase();

      const parsedMaxCohorts =
        maxCohorts === ""
          ? null
          : Number(maxCohorts);

      const parsedMaxLearners =
        maxLearners === ""
          ? null
          : Number(maxLearners);

      if (
        parsedMaxCohorts !== null &&
        (!Number.isInteger(
          parsedMaxCohorts
        ) ||
          parsedMaxCohorts < 0)
      ) {
        throw new Error(
          "Maximum cohorts must be a whole number of 0 or more, or left blank for unlimited."
        );
      }

      if (
        parsedMaxLearners !== null &&
        (!Number.isInteger(
          parsedMaxLearners
        ) ||
          parsedMaxLearners < 0)
      ) {
        throw new Error(
          "Maximum learners must be a whole number of 0 or more, or left blank for unlimited."
        );
      }

      await onSave({
        status: submittedStatus,

        /*
         * Keep any existing legacy allocation dates unchanged
         * for RPC compatibility. They no longer determine
         * organisation allocation status or cohort creation.
         */
        startsAt:
          programmeAccess?.starts_at ||
          null,

        endsAt:
          programmeAccess?.ends_at ||
          null,

        maxCohorts:
          parsedMaxCohorts,

        maxLearners:
          parsedMaxLearners,

        sponsoredAccessType,
      });
    } catch (submitError) {
      setFormError(
        submitError?.message ||
          "The organisation allocation settings could not be saved."
      );
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-[28px] bg-white p-5 shadow-2xl md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Organisation allocation
            </p>

            <h2 className="mt-2 text-2xl font-black text-[#333333]">
              Manage {partner.name}
            </h2>

            <p className="mt-2 text-sm leading-relaxed text-[#757575]">
              Configure this organisation&apos;s cohort allocation, sponsored learner allocation and learning access. This is separate from the organisation&apos;s portal status.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border border-[#E5E5E5] px-4 py-2 text-sm font-black text-[#555555] transition hover:bg-[#F8F8F8] disabled:opacity-50"
          >
            Close
          </button>
        </div>

        {formError && (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {formError}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="mt-6 space-y-5"
        >
          <div>
            <label
              htmlFor="allocation-status"
              className="block text-sm font-black text-[#333333]"
            >
              Allocation status
            </label>

            <select
              id="allocation-status"
              name="allocation-status"
              value={status}
              onChange={(event) =>
                setStatus(
                  event.target.value
                )
              }
              disabled={saving}
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="draft">
                Draft
              </option>
              <option value="active">
                Active
              </option>
              <option value="suspended">
                Suspended
              </option>
            </select>

            <p className="mt-2 text-xs leading-relaxed text-[#757575]">
              Active allows new cohorts within the allocation. Suspended keeps existing cohort information available but blocks new cohort creation.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="programme-max-cohorts"
                className="block text-sm font-black text-[#333333]"
              >
                Maximum cohorts
              </label>

              <input
                id="programme-max-cohorts"
                type="number"
                min="0"
                step="1"
                value={maxCohorts}
                onChange={(event) =>
                  setMaxCohorts(
                    event.target.value
                  )
                }
                disabled={saving}
                placeholder="Unlimited"
                className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />

              <p className="mt-2 text-xs text-[#757575]">
                Leave blank for unlimited. Enter 0 to allow no new cohorts.
              </p>
            </div>

            <div>
              <label
                htmlFor="programme-max-learners"
                className="block text-sm font-black text-[#333333]"
              >
                Maximum sponsored learners
              </label>

              <input
                id="programme-max-learners"
                type="number"
                min="0"
                step="1"
                value={maxLearners}
                onChange={(event) =>
                  setMaxLearners(
                    event.target.value
                  )
                }
                disabled={saving}
                placeholder="Unlimited"
                className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />

              <p className="mt-2 text-xs text-[#757575]">
                Leave blank for unlimited. Enter 0 to allow no sponsored learners.
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="programme-sponsored-access"
              className="block text-sm font-black text-[#333333]"
            >
              Sponsored learning access
            </label>

            <select
              id="programme-sponsored-access"
              value={sponsoredAccessType}
              onChange={(event) =>
                setSponsoredAccessType(
                  event.target.value
                )
              }
              disabled={saving}
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="none">
                No sponsored learning
              </option>

              <option value="full_journey">
                Full journey
              </option>
            </select>
          </div>

          <div className="rounded-2xl border border-[#FFE1CF] bg-[#FFF9F5] px-4 py-3">
            <p className="text-xs font-semibold leading-relaxed text-[#757575]">
              Suspending the organisation allocation is separate from suspending the organisation itself. Existing cohorts and historical information remain intact.
            </p>
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-full border border-[#E5E5E5] px-5 py-2.5 text-sm font-black text-[#555555] transition hover:bg-[#F8F8F8] disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:opacity-50"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving
                ? "Saving…"
                : "Save allocation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminPartnerOrganisations() {
  const [
    partners,
    setPartners,
  ] = useState([]);

  const [
    selectedPartnerId,
    setSelectedPartnerId,
  ] = useState("");

  const [search, setSearch] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [warning, setWarning] =
    useState("");

  const [
    createModalOpen,
    setCreateModalOpen,
  ] = useState(false);

  const [
    inviteModalOpen,
    setInviteModalOpen,
  ] = useState(false);

  const [
    editingName,
    setEditingName,
  ] = useState("");

  const [
    savingName,
    setSavingName,
  ] = useState(false);

  const [
    changingStatus,
    setChangingStatus,
  ] = useState(false);

  const [
    invitationRefreshKey,
    setInvitationRefreshKey,
  ] = useState(0);

  const [
    programmeAccess,
    setProgrammeAccess,
  ] = useState(null);

  const [
    programmeLoading,
    setProgrammeLoading,
  ] = useState(false);

  const [
    programmeModalOpen,
    setProgrammeModalOpen,
  ] = useState(false);

  const [
    programmeSaving,
    setProgrammeSaving,
  ] = useState(false);

  const loadPartners =
    useCallback(
      async (
        preferredPartnerId = ""
      ) => {
        try {
          setLoading(true);
          setError("");

          const rows =
            await fetchAdminPartnerOrganisations();

          setPartners(rows);

          setSelectedPartnerId(
            (current) => {
              const desired =
                preferredPartnerId ||
                current;

              if (
                desired &&
                rows.some(
                  (partner) =>
                    partner.id ===
                    desired
                )
              ) {
                return desired;
              }

              return (
                rows[0]?.id || ""
              );
            }
          );
        } catch (loadError) {
          console.error(
            "Loading admin partner organisations failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "The partner organisations could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      []
    );

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const loadProgrammeAccess =
    useCallback(
      async (partnerId) => {
        if (!partnerId) {
          setProgrammeAccess(null);
          setProgrammeLoading(false);
          return;
        }

        try {
          setProgrammeLoading(true);

          const result =
            await fetchAdminPartnerProgrammeAccess(
              partnerId
            );

          setProgrammeAccess(result);
        } catch (loadError) {
          console.error(
            "Loading organisation allocation failed:",
            loadError
          );

          setProgrammeAccess(null);
          setError(
            loadError?.message ||
              "The organisation allocation settings could not be loaded."
          );
        } finally {
          setProgrammeLoading(false);
        }
      },
      []
    );

  useEffect(() => {
    setProgrammeModalOpen(false);

    if (!selectedPartnerId) {
      setProgrammeAccess(null);
      return;
    }

    loadProgrammeAccess(
      selectedPartnerId
    );
  }, [
    loadProgrammeAccess,
    selectedPartnerId,
  ]);

  const selectedPartner =
    useMemo(
      () =>
        partners.find(
          (partner) =>
            partner.id ===
            selectedPartnerId
        ) || null,
      [
        partners,
        selectedPartnerId,
      ]
    );

  useEffect(() => {
    setEditingName(
      selectedPartner?.name || ""
    );
  }, [selectedPartner]);

  const filteredPartners =
    useMemo(() => {
      const query = search
        .trim()
        .toLowerCase();

      return partners.filter(
        (partner) => {
          const matchesStatus =
            statusFilter === "all" ||
            partner.status ===
              statusFilter;

          const matchesSearch =
            !query ||
            [
              partner.name,
              partner.slug,
              partner.created_by_email,
            ].some((value) =>
              String(value || "")
                .toLowerCase()
                .includes(query)
            );

          return (
            matchesStatus &&
            matchesSearch
          );
        }
      );
    }, [
      partners,
      search,
      statusFilter,
    ]);

  const summary = useMemo(
    () =>
      partners.reduce(
        (totals, partner) => {
          totals.organisations += 1;

          if (
            partner.status ===
            "active"
          ) {
            totals.active += 1;
          }

          totals.cohorts +=
            partner.cohort_count;

          totals.learners +=
            partner.learner_count;

          return totals;
        },
        {
          organisations: 0,
          active: 0,
          cohorts: 0,
          learners: 0,
        }
      ),
    [partners]
  );

  async function handleCreated({
    partner,
    invitation,
    invitationError,
  }) {
    const partnerId =
      partner?.partner_id;

    setMessage("");
    setWarning("");

    await loadPartners(
      partnerId || ""
    );

    if (invitationError) {
      setWarning(
        `${partner?.name || "The organisation"} was created, but the first invitation could not be sent: ${
          invitationError?.message ||
          "Unknown invitation error"
        }`
      );

      return;
    }

    setMessage(
      `${partner?.name || "The organisation"} was created and an invitation was sent to ${
        invitation?.email ||
        "the first manager"
      }.`
    );

    setInvitationRefreshKey(
      (current) => current + 1
    );
  }

  async function handleInvited({
    invitation,
  }) {
    setError("");
    setWarning("");

    setMessage(
      `An invitation was sent to ${
        invitation?.email ||
        "the partner manager"
      }.`
    );

    setInvitationRefreshKey(
      (current) => current + 1
    );

    await loadPartners(
      selectedPartner?.id || ""
    );
  }

  async function handleSaveName(
    event
  ) {
    event.preventDefault();

    if (!selectedPartner) {
      return;
    }

    try {
      setSavingName(true);
      setError("");
      setMessage("");
      setWarning("");

      await updateAdminPartnerOrganisation({
        partnerId:
          selectedPartner.id,

        name: editingName,

        status: null,
      });

      setMessage(
        "The partner organisation name was updated."
      );

      await loadPartners(
        selectedPartner.id
      );
    } catch (saveError) {
      console.error(
        "Updating partner name failed:",
        saveError
      );

      setError(
        saveError?.message ||
          "The partner organisation name could not be updated."
      );
    } finally {
      setSavingName(false);
    }
  }

  async function handleSaveProgrammeAccess(
    values
  ) {
    if (!selectedPartner) {
      return;
    }

    try {
      setProgrammeSaving(true);
      setError("");
      setMessage("");
      setWarning("");

      const result =
        await setAdminPartnerProgrammeAccess({
          partnerId:
            selectedPartner.id,
          status:
            values.status,
          startsAt:
            values.startsAt,
          endsAt:
            values.endsAt,
          maxCohorts:
            values.maxCohorts,
          maxLearners:
            values.maxLearners,
          sponsoredAccessType:
            values.sponsoredAccessType,
        });

      setProgrammeAccess(result);
      setProgrammeModalOpen(false);

      setMessage(
        `Organisation allocation for ${selectedPartner.name} was updated.`
      );
    } catch (saveError) {
      console.error(
        "Updating organisation allocation failed:",
        saveError
      );

      throw saveError;
    } finally {
      setProgrammeSaving(false);
    }
  }

  async function handleToggleStatus() {
    if (!selectedPartner) {
      return;
    }

    const nextStatus =
      selectedPartner.status ===
      "active"
        ? "suspended"
        : "active";

    const confirmed =
  window.confirm(
    nextStatus === "suspended"
      ? `Suspend organisation ${selectedPartner.name}? Partner users may lose access to the Partner portal. This is separate from the organisation allocation.`
      : `Reactivate organisation ${selectedPartner.name}?`
  );

    if (!confirmed) return;

    try {
      setChangingStatus(true);
      setError("");
      setMessage("");
      setWarning("");

      await updateAdminPartnerOrganisation({
        partnerId:
          selectedPartner.id,

        name: null,

        status: nextStatus,
      });

      setMessage(
        nextStatus === "active"
          ? `${selectedPartner.name} was reactivated.`
          : `${selectedPartner.name} was suspended.`
      );

      await loadPartners(
        selectedPartner.id
      );
    } catch (statusError) {
      console.error(
        "Updating partner status failed:",
        statusError
      );

      setError(
        statusError?.message ||
          "The partner organisation status could not be updated."
      );
    } finally {
      setChangingStatus(false);
    }
  }

  return (
    <>
      <header className="mb-7">
        <Link
          to="/admin"
          className="text-sm font-bold text-[#FF914D] hover:text-[#E9782F]"
        >
          ← Back to admin
        </Link>

        <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#FF914D]">
              Admin · Partner Organisations
            </p>

            <h1 className="mt-2 text-[34px] font-black leading-tight tracking-[-0.03em] text-[#333333] md:text-[42px]">
              Partner Organisations
            </h1>

            <p className="mt-2 max-w-4xl text-sm leading-relaxed text-[#757575] md:text-base">
              Create partner
              organisations, invite their
              first managers, monitor
              cohorts and learners, and
              control organisation access.
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              setCreateModalOpen(true)
            }
            className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create organisation
          </button>
        </div>
      </header>

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
        >
          {error}
        </div>
      )}

      {warning && (
        <div
          role="alert"
          className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
          {warning}
        </div>
      )}

      {message && (
        <div
          role="status"
          className="mb-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800"
        >
          {message}
        </div>
      )}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Organisations"
          value={
            summary.organisations
          }
          detail="Total partner organisations"
          icon={Building2}
        />

        <SummaryCard
          label="Active"
          value={summary.active}
          detail="Active organisations"
          icon={CheckCircle2}
        />

        <SummaryCard
          label="Cohorts"
          value={summary.cohorts}
          detail="Across all partners"
          icon={Layers3}
        />

        <SummaryCard
          label="Learners"
          value={summary.learners}
          detail="Unique cohort learners"
          icon={Users}
        />
      </section>

      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#BCBCBC]" />

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search by organisation, slug, or creator..."
              className="w-full rounded-full border border-[#E5E5E5] bg-white py-3 pl-11 pr-4 text-sm font-semibold text-[#333333] outline-none focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
              className="rounded-full border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-bold text-[#333333] outline-none focus:border-[#FF914D]"
            >
              <option value="all">
                All statuses
              </option>

              <option value="active">
                Active
              </option>

              <option value="suspended">
                Suspended
              </option>
            </select>

            <button
              type="button"
              onClick={() => {
                loadPartners(
                  selectedPartnerId
                );

                if (selectedPartnerId) {
                  loadProgrammeAccess(
                    selectedPartnerId
                  );
                }
              }}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-full border border-[#FFE1CF] bg-white px-5 py-3 text-sm font-bold text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:opacity-60"
            >
              <RefreshCw
                className={[
                  "mr-2 h-4 w-4",
                  loading
                    ? "animate-spin"
                    : "",
                ].join(" ")}
              />

              Refresh
            </button>
          </div>
        </div>
      </section>

      <section className="mt-5 overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[1450px] w-full border-collapse text-left text-sm">
            <thead className="bg-[#FFF4EC] text-xs uppercase tracking-wider text-[#757575]">
              <tr>
                <th className="px-5 py-4 font-black">
                  Organisation
                </th>

                <th className="px-5 py-4 font-black">
                  Organisation status
                </th>

                <th className="px-5 py-4 text-right font-black">
                  Team
                </th>

                <th className="px-5 py-4 text-right font-black">
                  Owners / Admins
                </th>

                <th className="px-5 py-4 text-right font-black">
                  Cohorts
                </th>

                <th className="px-5 py-4 text-right font-black">
                  Learners
                </th>

                <th className="px-5 py-4 text-right font-black">
                  Pending invites
                </th>

                <th className="px-5 py-4 font-black">
                  Created
                </th>

                <th className="px-5 py-4 text-right font-black">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-5 py-10 text-center font-semibold text-[#757575]"
                  >
                    Loading partner
                    organisations…
                  </td>
                </tr>
              ) : filteredPartners.length ===
                0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-5 py-10 text-center font-semibold text-[#757575]"
                  >
                    No partner
                    organisations found.
                  </td>
                </tr>
              ) : (
                filteredPartners.map(
                  (partner) => {
                    const selected =
                      partner.id ===
                      selectedPartnerId;

                    const pendingInvites =
                      partner.pending_team_invitation_count +
                      partner.pending_learner_invitation_count;

                    return (
                      <tr
                        key={partner.id}
                        className={[
                          "border-t border-[#EFEFEF] align-top transition",
                          selected
                            ? "bg-[#FFF9F5]"
                            : "hover:bg-[#FFFCFA]",
                        ].join(" ")}
                      >
                        <td className="px-5 py-4">
                          <p className="font-black text-[#333333]">
                            {partner.name}
                          </p>

                          <p className="mt-1 text-xs font-semibold text-[#757575]">
                            {partner.slug}
                          </p>

                          <p className="mt-1 text-[11px] text-[#999999]">
                            {partner.id}
                          </p>
                        </td>

                        <td className="px-5 py-4">
  <span
    className={[
      "inline-flex rounded-full border px-3 py-1 text-xs font-black capitalize",
      statusClasses(
        partner.status
      ),
    ].join(" ")}
  >
    {partner.status}
  </span>

  <p className="mt-1 text-[11px] font-semibold text-[#999999]">
    Partner portal access
  </p>
</td>

                        <td className="px-5 py-4 text-right">
                          <p className="font-black">
                            {formatNumber(
                              partner.team_member_count
                            )}
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            {
                              partner.active_team_member_count
                            }{" "}
                            active
                          </p>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <p className="font-black">
                            {partner.owner_count}{" "}
                            /{" "}
                            {partner.admin_count}
                          </p>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <p className="font-black">
                            {
                              partner.cohort_count
                            }
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            {
                              partner.active_cohort_count
                            }{" "}
                            active
                          </p>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <p className="font-black">
                            {
                              partner.learner_count
                            }
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            {
                              partner.active_learner_count
                            }{" "}
                            active
                          </p>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <p className="font-black">
                            {pendingInvites}
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            {
                              partner.pending_team_invitation_count
                            }{" "}
                            team ·{" "}
                            {
                              partner.pending_learner_invitation_count
                            }{" "}
                            learner
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p className="font-semibold">
                            {formatDateTime(
                              partner.created_at
                            )}
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            {partner.created_by_email ||
                              "Unknown creator"}
                          </p>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedPartnerId(
                                partner.id
                              )
                            }
                            className={[
                              "rounded-full px-4 py-2 text-xs font-black transition",
                              selected
                                ? "bg-[#333333] text-white"
                                : "bg-[#FF914D] text-white hover:bg-[#E9782F]",
                            ].join(" ")}
                          >
                            {selected
                              ? "Selected"
                              : "View details"}
                          </button>
                        </td>
                      </tr>
                    );
                  }
                )
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedPartner && (
        <section className="mt-7 rounded-[28px] border border-[#EFEFEF] bg-white p-5 shadow-sm md:p-6">
          <header className="flex flex-col gap-5 border-b border-[#EFEFEF] pb-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                Selected organisation
              </p>

              <h2 className="mt-2 text-2xl font-black text-[#333333]">
                {selectedPartner.name}
              </h2>

              <p className="mt-1 text-sm text-[#757575]">
                {selectedPartner.slug}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
  <span
    className={[
      "inline-flex rounded-full border px-3 py-1 text-xs font-black",
      statusClasses(
        selectedPartner.status
      ),
    ].join(" ")}
  >
    Organisation:{" "}
    {programmeStatusLabel(
      selectedPartner.status
    )}
  </span>

  {!programmeLoading &&
    programmeAccess && (
      <span
        className={[
          "inline-flex rounded-full border px-3 py-1 text-xs font-black",
          programmeStatusClasses(
            programmeAccess.effective_status
          ),
        ].join(" ")}
      >
        Allocation:{" "}
        {programmeStatusLabel(
          programmeAccess.effective_status
        )}
      </span>
    )}
</div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  setInviteModalOpen(true)
                }
                disabled={
                  selectedPartner.status !==
                  "active"
                }
                className="inline-flex items-center rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Invite owner/admin
              </button>

              <button
                type="button"
                onClick={
                  handleToggleStatus
                }
                disabled={
                  changingStatus
                }
                className={[
                  "inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-black transition disabled:opacity-50",
                  selectedPartner.status ===
                  "active"
                    ? "border-red-200 text-red-700 hover:bg-red-50"
                    : "border-green-200 text-green-700 hover:bg-green-50",
                ].join(" ")}
              >
                {selectedPartner.status ===
                "active" ? (
                  <XCircle className="mr-2 h-4 w-4" />
                ) : (
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                )}

                {changingStatus
  ? "Updating…"
  : selectedPartner.status ===
    "active"
  ? "Suspend organisation"
  : "Reactivate organisation"}
              </button>
            </div>
          </header>

          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Team"
              value={
                selectedPartner.team_member_count
              }
              detail={`${selectedPartner.active_team_member_count} active`}
              icon={Users}
            />

            <SummaryCard
              label="Owners"
              value={
                selectedPartner.owner_count
              }
              detail={`${selectedPartner.admin_count} administrators`}
              icon={ShieldCheck}
            />

            <SummaryCard
              label="Cohorts"
              value={
                selectedPartner.cohort_count
              }
              detail={`${selectedPartner.active_cohort_count} active`}
              icon={Layers3}
            />

            <SummaryCard
              label="Learners"
              value={
                selectedPartner.learner_count
              }
              detail={`${selectedPartner.active_learner_count} active`}
              icon={UserPlus}
            />
          </div>

          <div className="mt-6 rounded-[24px] border border-[#FFE1CF] bg-[#FFF9F5] p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                  <ShieldCheck className="h-4 w-4" />
                </div>

                <div>
                  <h3 className="font-black text-[#333333]">
                    Organisation allocation
                  </h3>

                  <p className="mt-1 max-w-3xl text-xs leading-relaxed text-[#757575]">
  Controls this organisation's cohort allocation
  and sponsored learner allowance. Suspending
  the allocation blocks new cohort creation without
  suspending the organisation or changing the
  status of existing cohorts.
</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  setProgrammeModalOpen(true)
                }
                disabled={programmeLoading}
                className="inline-flex items-center justify-center rounded-full bg-[#333333] px-5 py-2.5 text-sm font-black text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Manage allocation
              </button>
            </div>

            {programmeLoading ? (
              <div className="mt-5 rounded-2xl border border-[#EFEFEF] bg-white px-4 py-6 text-sm font-semibold text-[#757575]">
                Loading organisation allocation…
              </div>
            ) : programmeAccess ? (
              <>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <span
                    className={[
                      "inline-flex rounded-full border px-3 py-1 text-xs font-black",
                      programmeStatusClasses(
                        programmeAccess.effective_status
                      ),
                    ].join(" ")}
                  >
                    {programmeStatusLabel(
                      programmeAccess.effective_status
                    )}
                  </span>

                  {programmeAccess.configured &&
                    programmeAccess.status !==
                      programmeAccess.effective_status && (
                      <span className="text-xs font-semibold text-[#757575]">
                        Configured status: {programmeStatusLabel(
                          programmeAccess.status
                        )}
                      </span>
                    )}
                </div>

                {programmeAccess.effective_status ===
  "suspended" && (
  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
    <div className="flex items-start gap-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />

      <div>
        <p className="text-sm font-black text-amber-900">
          Organisation allocation suspended
        </p>

        <p className="mt-1 text-xs font-semibold leading-relaxed text-amber-800">
          The organisation remains active
          and existing cohorts may still
          show as Active. Creating new
          cohorts is unavailable until
          the allocation is reactivated.
        </p>
      </div>
    </div>
  </div>
)}

                <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">

                  <div className="rounded-2xl border border-[#EFEFEF] bg-white p-4">
                    <p className="text-[11px] font-black uppercase tracking-wider text-[#757575]">
                      Cohorts
                    </p>

                    <p className="mt-2 text-lg font-black text-[#333333]">
                      {formatProgrammeUsage(
                        programmeAccess.cohort_usage,
                        programmeAccess.max_cohorts
                      )}
                    </p>

                    <p className="mt-1 text-xs text-[#757575]">
                      Used / allocation
                    </p>
                  </div>

                  <div className="rounded-2xl border border-[#EFEFEF] bg-white p-4">
                    <p className="text-[11px] font-black uppercase tracking-wider text-[#757575]">
                      Sponsored entrepreneurs
                    </p>

                    <p className="mt-2 text-lg font-black text-[#333333]">
                      {formatProgrammeUsage(
                        programmeAccess.learner_usage,
                        programmeAccess.max_learners
                      )}
                    </p>

                    <p className="mt-1 text-xs text-[#757575]">
                      Used / allocation
                    </p>
                  </div>

                  <div className="rounded-2xl border border-[#EFEFEF] bg-white p-4">
                    <p className="text-[11px] font-black uppercase tracking-wider text-[#757575]">
                      Learning access
                    </p>

                    <p className="mt-2 text-sm font-black text-[#333333]">
                      {sponsoredAccessLabel(
                        programmeAccess.sponsored_access_type
                      )}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-[#EFEFEF] bg-white p-4">
                    <p className="text-[11px] font-black uppercase tracking-wider text-[#757575]">
                      Last updated
                    </p>

                    <p className="mt-2 text-sm font-black text-[#333333]">
                      {formatDateTime(
                        programmeAccess.updated_at
                      )}
                    </p>
                  </div>
                </div>

                {!programmeAccess.configured && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold leading-relaxed text-amber-800">
                    No organisation allocation has been configured yet. New cohort creation remains unavailable until an allocation is configured.
                  </div>
                )}
              </>
            ) : (
              <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                Organisation allocation information could not be loaded.
              </div>
            )}
          </div>

          <div className="mt-6 grid gap-5 xl:grid-cols-2">
            <form
              onSubmit={
                handleSaveName
              }
              className="rounded-[24px] border border-[#EFEFEF] bg-[#FAFAFA] p-5"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                  <Pencil className="h-4 w-4" />
                </div>

                <div>
                  <h3 className="font-black text-[#333333]">
                    Organisation details
                  </h3>

                  <p className="text-xs text-[#757575]">
                    Update the display name.
                    The slug remains unchanged.
                  </p>
                </div>
              </div>

              <label
                htmlFor="admin-edit-partner-name"
                className="mt-5 block text-sm font-black text-[#333333]"
              >
                Organisation name
              </label>

              <input
                id="admin-edit-partner-name"
                type="text"
                value={editingName}
                onChange={(event) =>
                  setEditingName(
                    event.target.value
                  )
                }
                disabled={savingName}
                minLength={2}
                maxLength={150}
                required
                className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />

              <button
                type="submit"
                disabled={
                  savingName ||
                  editingName.trim()
                    .length < 2 ||
                  editingName.trim() ===
                    selectedPartner.name
                }
                className="mt-4 inline-flex items-center rounded-full bg-[#333333] px-5 py-2.5 text-sm font-black text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Save className="mr-2 h-4 w-4" />

                {savingName
                  ? "Saving…"
                  : "Save name"}
              </button>
            </form>

            <div className="rounded-[24px] border border-[#EFEFEF] bg-[#FAFAFA] p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                  <Building2 className="h-4 w-4" />
                </div>

                <div>
                  <h3 className="font-black text-[#333333]">
                    Organisation record
                  </h3>

                  <p className="text-xs text-[#757575]">
                    Internal creation and
                    status information.
                  </p>
                </div>
              </div>

              <dl className="mt-5 space-y-3 text-sm">
                <div>
                  <dt className="text-xs font-black uppercase tracking-wider text-[#757575]">
                    Organisation status
                  </dt>

                  <dd className="mt-1 font-bold capitalize text-[#333333]">
                    {
                      selectedPartner.status
                    }
                  </dd>
                </div>

                <div>
                  <dt className="text-xs font-black uppercase tracking-wider text-[#757575]">
                    Created by
                  </dt>

                  <dd className="mt-1 font-bold text-[#333333]">
                    {selectedPartner.created_by_email ||
                      "Unknown"}
                  </dd>
                </div>

                <div>
                  <dt className="text-xs font-black uppercase tracking-wider text-[#757575]">
                    Created
                  </dt>

                  <dd className="mt-1 font-bold text-[#333333]">
                    {formatDateTime(
                      selectedPartner.created_at
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-xs font-black uppercase tracking-wider text-[#757575]">
                    Last updated
                  </dt>

                  <dd className="mt-1 font-bold text-[#333333]">
                    {formatDateTime(
                      selectedPartner.updated_at
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-[#FF914D]" />

              <div>
                <h3 className="font-black text-[#333333]">
                  Partner manager
                  invitations
                </h3>

                <p className="text-sm text-[#757575]">
                  Review, resend or revoke
                  owner and team invitations.
                </p>
              </div>
            </div>

            <PartnerMemberInvitationsPanel
              partnerId={
                selectedPartner.id
              }
              canManage
              refreshKey={
                invitationRefreshKey
              }
              onChanged={() =>
                loadPartners(
                  selectedPartner.id
                )
              }
            />
          </div>
        </section>
      )}

      <CreatePartnerOrganisationModal
        open={createModalOpen}
        onClose={() =>
          setCreateModalOpen(false)
        }
        onCreated={handleCreated}
      />

      <AdminInvitePartnerMemberModal
        open={inviteModalOpen}
        partner={selectedPartner}
        onClose={() =>
          setInviteModalOpen(false)
        }
        onInvited={handleInvited}
      />

      <ProgrammeAccessModal
        open={programmeModalOpen}
        partner={selectedPartner}
        programmeAccess={programmeAccess}
        saving={programmeSaving}
        onClose={() =>
          setProgrammeModalOpen(false)
        }
        onSave={
          handleSaveProgrammeAccess
        }
      />
    </>
  );
}